import React, { useEffect, useState } from 'react';
import { Upload, Trash2, Image as ImageIcon, Save, RefreshCw, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Sleek single-tab brand-asset manager.
 * Drives:
 *   - Journey Deck PDF logo (top-right of every page)
 *   - Admin email signature logo (mirrors into signature_settings.logo_url)
 *
 * Both stored in Mongo `brand_assets` singleton via /api/admin/brand-assets.
 * Uploads use the existing /api/upload/logo Cloudinary endpoint.
 */
export const BrandAssetsTab = ({ getStoredPassword }) => {
  const [data, setData] = useState({ deck_logo_url: '', email_signature_logo_url: '', city_banners: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyKey, setBusyKey] = useState(null); // which field is currently uploading

  // City banner draft
  const [newCity, setNewCity] = useState('');
  const [newCityUrl, setNewCityUrl] = useState('');
  const [busyBannerIdx, setBusyBannerIdx] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const pwd = encodeURIComponent(getStoredPassword?.() || '');
      const r = await fetch(`${API_URL}/api/admin/brand-assets?password=${pwd}`);
      if (r.ok) {
        const d = await r.json();
        setData({
          deck_logo_url: d.deck_logo_url || '',
          email_signature_logo_url: d.email_signature_logo_url || '',
          city_banners: Array.isArray(d.city_banners) ? d.city_banners : [],
        });
      }
    } catch { toast.error('Failed to load brand assets'); }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    setSaving(true);
    try {
      const pwd = encodeURIComponent(getStoredPassword?.() || '');
      const r = await fetch(`${API_URL}/api/admin/brand-assets?password=${pwd}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          deck_logo_url: data.deck_logo_url || '',
          email_signature_logo_url: data.email_signature_logo_url || '',
          city_banners: data.city_banners || [],
        }),
      });
      if (r.ok) {
        toast.success('Brand assets saved — Journey Deck will pick up the new logo immediately');
        await load();
      } else {
        toast.error('Save failed');
      }
    } catch { toast.error('Network error'); }
    setSaving(false);
  };

  const upload = async (key, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be < 5 MB');
      return;
    }
    setBusyKey(key);
    try {
      const pwd = encodeURIComponent(getStoredPassword?.() || '');
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${API_URL}/api/upload/logo?password=${pwd}`, { method: 'POST', body: fd });
      const d = await r.json();
      if (r.ok && d.logo_url) {
        setData((prev) => ({ ...prev, [key]: d.logo_url }));
        toast.success('Uploaded — click "Save changes" to apply');
      } else {
        toast.error(d.detail || 'Upload failed');
      }
    } catch { toast.error('Network error'); }
    setBusyKey(null);
  };

  const clear = (key) => setData((p) => ({ ...p, [key]: '' }));

  // ---- City banner helpers ----
  const addCityBanner = () => {
    const city = newCity.trim();
    const url = newCityUrl.trim();
    if (!city || !url) {
      toast.error('Both city/country name and image URL are required');
      return;
    }
    if (data.city_banners.some(b => b.city.toLowerCase() === city.toLowerCase())) {
      toast.error(`A banner for "${city}" already exists`);
      return;
    }
    setData(p => ({ ...p, city_banners: [...p.city_banners, { city, url, stage_url: '', enabled: true }] }));
    setNewCity(''); setNewCityUrl('');
  };

  const toggleCityBanner = (idx) => {
    setData(p => ({
      ...p,
      city_banners: p.city_banners.map((b, i) => i === idx ? { ...b, enabled: !b.enabled } : b),
    }));
  };

  const removeCityBanner = (idx) => {
    setData(p => ({ ...p, city_banners: p.city_banners.filter((_, i) => i !== idx) }));
  };

  const uploadCityBannerImage = async (idx, field, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be < 5 MB');
      return;
    }
    setBusyBannerIdx(`${idx}-${field}`);
    try {
      const pwd = encodeURIComponent(getStoredPassword?.() || '');
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${API_URL}/api/upload/logo?password=${pwd}`, { method: 'POST', body: fd });
      const d = await r.json();
      if (r.ok && d.logo_url) {
        setData(p => ({
          ...p,
          city_banners: p.city_banners.map((b, i) => i === idx ? { ...b, [field]: d.logo_url } : b),
        }));
        toast.success('Uploaded — click "Save changes" to apply');
      } else {
        toast.error(d.detail || 'Upload failed');
      }
    } catch { toast.error('Network error'); }
    setBusyBannerIdx(null);
  };

  const ASSETS = [
    {
      key: 'deck_logo_url',
      title: 'Journey Deck Logo',
      caption: 'Top-right of every page in the 7-page Client Journey Deck PDF (and PDF→PNG preview).',
      tip: 'Best as gold-tone PNG with transparent background, square or near-square aspect ratio (e.g., 283×273).',
    },
    {
      key: 'email_signature_logo_url',
      title: 'Email Signature Logo',
      caption: 'Embedded in admin email signatures (auto-replies, journey-deck attachments, deal alerts).',
      tip: 'Recommended: 240px wide, transparent PNG. Mirrors automatically to signature settings.',
    },
  ];

  return (
    <div className="space-y-5" data-testid="brand-assets-tab">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-1">Brand Assets</div>
          <h2 className="text-xl font-semibold text-white">Logos & Marks</h2>
          <p className="text-[12px] text-white/50 mt-1 max-w-2xl">
            Manage the brand artwork that appears in client-facing PDFs and admin email signatures.
            Uploaded files go straight to Cloudinary; changes apply on the very next render.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={load}
            disabled={loading}
            data-testid="brand-assets-refresh"
            className="px-3 py-1.5 rounded-md border border-white/10 hover:border-[#D4AF37]/60 text-white text-[12px] flex items-center gap-1.5 transition"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <Button
            onClick={save}
            disabled={saving}
            data-testid="brand-assets-save"
            className="bg-[#D4AF37] text-black hover:bg-[#B8941E]"
          >
            <Save className="w-4 h-4 mr-1.5" /> {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {ASSETS.map(({ key, title, caption, tip }) => {
          const url = data[key] || '';
          const inputId = `brand-asset-input-${key}`;
          return (
            <div
              key={key}
              data-testid={`brand-asset-${key}`}
              className="bg-white/[0.03] border border-white/10 rounded-lg p-5 space-y-4"
            >
              <div>
                <h3 className="text-white font-semibold text-sm">{title}</h3>
                <p className="text-[11px] text-white/50 mt-1 leading-relaxed">{caption}</p>
              </div>

              {/* Preview */}
              <div
                className="relative bg-[#0a1a14] border border-white/10 rounded-md p-6 flex items-center justify-center min-h-[160px]"
                data-testid={`brand-asset-preview-${key}`}
              >
                {url ? (
                  <img
                    src={url}
                    alt={`${title} preview`}
                    className="max-h-32 max-w-full object-contain"
                    onError={(e) => { e.currentTarget.style.opacity = '0.3'; }}
                  />
                ) : (
                  <div className="text-center text-white/30">
                    <ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-60" />
                    <div className="text-[11px] uppercase tracking-[0.15em]">No logo configured</div>
                  </div>
                )}
              </div>

              {/* URL input */}
              <div>
                <label className="text-[10px] uppercase tracking-[0.18em] text-white/40 block mb-1.5">Cloudinary URL</label>
                <div className="flex gap-1.5">
                  <input
                    value={url}
                    onChange={(e) => setData((p) => ({ ...p, [key]: e.target.value }))}
                    placeholder="https://res.cloudinary.com/..."
                    spellCheck={false}
                    className="flex-1 bg-[#0a1a14] border border-white/10 rounded px-3 py-2 text-white text-[12px] font-mono focus:outline-none focus:border-[#D4AF37]/60"
                    data-testid={`brand-asset-url-${key}`}
                  />
                  {url && (
                    <>
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-2.5 py-2 rounded border border-white/10 hover:border-[#D4AF37]/60 text-white/60 hover:text-white"
                        title="Open in new tab"
                        data-testid={`brand-asset-open-${key}`}
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                      <button
                        type="button"
                        onClick={() => clear(key)}
                        className="px-2.5 py-2 rounded border border-white/10 hover:border-red-400/60 text-white/60 hover:text-red-400"
                        title="Clear"
                        data-testid={`brand-asset-clear-${key}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Upload */}
              <div className="pt-1">
                <input
                  id={inputId}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  onChange={(e) => upload(key, e.target.files?.[0])}
                  className="hidden"
                  data-testid={`brand-asset-file-${key}`}
                />
                <label
                  htmlFor={inputId}
                  className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-md border text-[12px] cursor-pointer transition
                    ${busyKey === key
                      ? 'border-white/20 text-white/40 cursor-wait'
                      : 'border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10'}`}
                  data-testid={`brand-asset-upload-${key}`}
                >
                  <Upload className={`w-3.5 h-3.5 ${busyKey === key ? 'animate-pulse' : ''}`} />
                  {busyKey === key ? 'Uploading…' : 'Upload new image'}
                </label>
                <p className="text-[10px] text-white/30 mt-2 leading-relaxed">{tip}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* ========== CITY BANNERS ========== */}
      <div className="bg-white/[0.03] border border-white/10 rounded-lg p-5 space-y-4" data-testid="city-banners-section">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <h3 className="text-white font-semibold text-sm">Cover Page City Banners</h3>
            <p className="text-[11px] text-white/50 mt-1 leading-relaxed max-w-2xl">
              When a bespoke brief lists a target city/country that matches one of these,
              the Journey Deck cover auto-uses the matching hero photo as a subtly tinted
              background behind "Prepared for [Name]". Falls back to plain dark-green if
              no match is found. Use a <code className="text-[#D4AF37]">default</code> entry as a universal fallback.
            </p>
          </div>
          <span className="text-[10px] uppercase tracking-[0.18em] text-white/40 shrink-0">
            {(data.city_banners || []).length} configured
          </span>
        </div>

        {/* List */}
        {(data.city_banners || []).length === 0 ? (
          <p className="text-white/40 text-[12px] italic py-3 text-center bg-white/[0.02] rounded">
            No city banners configured yet — the cover will use the standard dark-green canvas.
          </p>
        ) : (
          <div className="space-y-2">
            {data.city_banners.map((b, idx) => {
              const inputId = `city-banner-file-${idx}`;
              const stageInputId = `city-banner-stage-file-${idx}`;
              return (
                <div
                  key={idx}
                  data-testid={`city-banner-${idx}`}
                  className="bg-[#0a1a14] border border-white/10 rounded-md p-3 space-y-2.5"
                >
                  {/* Top row: enable, thumb, city name, delete */}
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={!!b.enabled}
                      onChange={() => toggleCityBanner(idx)}
                      className="accent-[#D4AF37] w-4 h-4 shrink-0"
                      data-testid={`city-banner-toggle-${idx}`}
                    />
                    <div className="w-16 h-12 rounded bg-black/40 border border-white/10 overflow-hidden shrink-0">
                      {b.url ? (
                        <img src={b.url} alt={`${b.city} thumbnail`} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.opacity = '0.3'; }} />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-white/30">
                          <ImageIcon className="w-4 h-4" />
                        </div>
                      )}
                    </div>
                    <input
                      value={b.city}
                      onChange={(e) => setData(p => ({
                        ...p, city_banners: p.city_banners.map((x, i) => i === idx ? { ...x, city: e.target.value } : x),
                      }))}
                      placeholder="City / country"
                      className={`flex-1 bg-black/40 border border-white/10 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:border-[#D4AF37]/60 ${b.enabled ? 'text-white' : 'text-white/40'}`}
                      data-testid={`city-banner-city-${idx}`}
                    />
                    <button
                      type="button"
                      onClick={() => removeCityBanner(idx)}
                      className="text-white/40 hover:text-red-400 p-1.5 shrink-0"
                      title="Remove"
                      data-testid={`city-banner-delete-${idx}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  {/* Cover image row */}
                  <div className="flex items-center gap-2 pl-7">
                    <span className="text-[10px] uppercase tracking-[0.15em] text-white/40 w-12 shrink-0">Cover</span>
                    <input
                      value={b.url}
                      onChange={(e) => setData(p => ({
                        ...p, city_banners: p.city_banners.map((x, i) => i === idx ? { ...x, url: e.target.value } : x),
                      }))}
                      placeholder="https://… (full hero shot)"
                      spellCheck={false}
                      className={`flex-1 bg-black/40 border border-white/10 rounded px-2.5 py-1.5 text-[11px] font-mono focus:outline-none focus:border-[#D4AF37]/60 ${b.enabled ? 'text-white' : 'text-white/40'}`}
                      data-testid={`city-banner-url-${idx}`}
                    />
                    <input
                      id={inputId}
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      onChange={(e) => uploadCityBannerImage(idx, 'url', e.target.files?.[0])}
                      className="hidden"
                    />
                    <label
                      htmlFor={inputId}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded border text-[11px] cursor-pointer transition shrink-0
                        ${busyBannerIdx === `${idx}-url`
                          ? 'border-white/20 text-white/40 cursor-wait'
                          : 'border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10'}`}
                      title="Upload cover image"
                      data-testid={`city-banner-upload-${idx}`}
                    >
                      <Upload className={`w-3.5 h-3.5 ${busyBannerIdx === `${idx}-url` ? 'animate-pulse' : ''}`} />
                      {busyBannerIdx === `${idx}-url` ? 'Up…' : 'Upload'}
                    </label>
                  </div>

                  {/* Stage accent row (optional — falls back to cover when empty) */}
                  <div className="flex items-center gap-2 pl-7">
                    <span className="text-[10px] uppercase tracking-[0.15em] text-white/40 w-12 shrink-0">Stage</span>
                    <input
                      value={b.stage_url || ''}
                      onChange={(e) => setData(p => ({
                        ...p, city_banners: p.city_banners.map((x, i) => i === idx ? { ...x, stage_url: e.target.value } : x),
                      }))}
                      placeholder="https://… (optional — 65×35mm corner accent on stage pages)"
                      spellCheck={false}
                      className={`flex-1 bg-black/40 border border-white/10 rounded px-2.5 py-1.5 text-[11px] font-mono focus:outline-none focus:border-[#D4AF37]/60 ${b.enabled ? 'text-white' : 'text-white/40'}`}
                      data-testid={`city-banner-stage-url-${idx}`}
                    />
                    <input
                      id={stageInputId}
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      onChange={(e) => uploadCityBannerImage(idx, 'stage_url', e.target.files?.[0])}
                      className="hidden"
                    />
                    <label
                      htmlFor={stageInputId}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded border text-[11px] cursor-pointer transition shrink-0
                        ${busyBannerIdx === `${idx}-stage_url`
                          ? 'border-white/20 text-white/40 cursor-wait'
                          : 'border-white/15 text-white/60 hover:border-[#D4AF37]/40 hover:text-[#D4AF37]'}`}
                      title="Upload stage accent image"
                      data-testid={`city-banner-stage-upload-${idx}`}
                    >
                      <Upload className={`w-3.5 h-3.5 ${busyBannerIdx === `${idx}-stage_url` ? 'animate-pulse' : ''}`} />
                      {busyBannerIdx === `${idx}-stage_url` ? 'Up…' : 'Upload'}
                    </label>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Add new */}
        <div className="border-t border-white/10 pt-4 grid grid-cols-1 sm:grid-cols-[1fr_2fr_auto] gap-2">
          <input
            value={newCity}
            onChange={(e) => setNewCity(e.target.value)}
            placeholder="City / country (or 'default')"
            className="bg-[#0a1a14] border border-white/10 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]/60"
            data-testid="city-banner-new-city"
          />
          <input
            value={newCityUrl}
            onChange={(e) => setNewCityUrl(e.target.value)}
            placeholder="Image URL (or use Upload after adding)"
            spellCheck={false}
            onKeyDown={(e) => { if (e.key === 'Enter') addCityBanner(); }}
            className="bg-[#0a1a14] border border-white/10 rounded px-3 py-2 text-white text-[12px] font-mono focus:outline-none focus:border-[#D4AF37]/60"
            data-testid="city-banner-new-url"
          />
          <Button
            onClick={addCityBanner}
            disabled={!newCity.trim() || !newCityUrl.trim()}
            className="bg-[#D4AF37] text-black hover:bg-[#B8941E]"
            data-testid="city-banner-add-btn"
          >
            Add
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BrandAssetsTab;
